import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import AIUsageService, { AIUsageConfig } from '../../services/AIUsageService';

interface AIUsageConfigurationProps {
  config: AIUsageConfig;
  onUpdate: () => void;
}

const AIUsageConfiguration: React.FC<AIUsageConfigurationProps> = ({ config, onUpdate }) => {
  const [dailyThreshold, setDailyThreshold] = useState(config.alert_thresholds.daily_usd.toString());
  const [monthlyThreshold, setMonthlyThreshold] = useState(config.alert_thresholds.monthly_usd.toString());
  const [adminKeyDialogOpen, setAdminKeyDialogOpen] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleUpdateThresholds = async () => {
    try {
      setLoading(true);
      setMessage(null);

      await AIUsageService.updateThresholds(
        parseFloat(dailyThreshold),
        parseFloat(monthlyThreshold)
      );

      setMessage({ type: 'success', text: 'Alert thresholds updated successfully' });
      setTimeout(() => onUpdate(), 1000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update thresholds'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureAdminKey = async () => {
    try {
      setLoading(true);
      setMessage(null);

      await AIUsageService.configureAdminKey('claude', adminKey);

      setMessage({ type: 'success', text: 'Claude Admin API key configured successfully' });
      setAdminKeyDialogOpen(false);
      setAdminKey('');
      setTimeout(() => onUpdate(), 1000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to configure admin key'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Provider Status */}
      <Box sx={{ width: '100%' }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Provider Status
            </Typography>
            <Divider sx={{ my: 2 }} />

            {/* Claude Status */}
            <Box mb={3}>
              <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Claude (Anthropic)
                </Typography>
                <Chip
                  size="small"
                  label={config.default_provider === 'claude' ? 'Default' : 'Available'}
                  color={config.default_provider === 'claude' ? 'primary' : 'default'}
                />
              </Stack>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {config.claude.api_key_configured ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="body2">
                      API Key: {config.claude.api_key_configured ? 'Configured' : 'Not Configured'}
                    </Typography>
                  </Stack>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {config.claude.admin_key_configured ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="body2">
                      Admin API Key: {config.claude.admin_key_configured ? 'Configured' : 'Not Configured'}
                    </Typography>
                  </Stack>
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {config.claude.usage_tracking_enabled ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="warning" fontSize="small" />
                    )}
                    <Typography variant="body2">
                      Usage Tracking: {config.claude.usage_tracking_enabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </Stack>
                </Box>
              </Box>

              {!config.claude.admin_key_configured && (
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={() => setAdminKeyDialogOpen(true)}
                  >
                    Configure Admin API Key
                  </Button>
                  <Typography variant="caption" display="block" color="text.secondary" mt={1}>
                    Admin API key is required for detailed usage tracking and cost reporting
                  </Typography>
                </Box>
              )}
            </Box>

            {/* OpenAI Status */}
            <Box>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    OpenAI
                  </Typography>
                  <Chip
                    size="small"
                    label={config.default_provider === 'openai' ? 'Default' : 'Available'}
                    color={config.default_provider === 'openai' ? 'primary' : 'default'}
                  />
                </Stack>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {config.openai.api_key_configured ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2">
                        API Key: {config.openai.api_key_configured ? 'Configured' : 'Not Configured'}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {config.openai.usage_tracking_enabled ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="warning" fontSize="small" />
                      )}
                      <Typography variant="body2">
                        Usage Tracking: {config.openai.usage_tracking_enabled ? 'Enabled' : 'Disabled'}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Alert Thresholds */}
        <Box sx={{ width: '100%' }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Alert Thresholds
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label="Daily Cost Alert (USD)"
                    type="number"
                    value={dailyThreshold}
                    onChange={(e) => setDailyThreshold(e.target.value)}
                    helperText="Alert when daily cost exceeds this amount"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                    }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    label="Monthly Cost Alert (USD)"
                    type="number"
                    value={monthlyThreshold}
                    onChange={(e) => setMonthlyThreshold(e.target.value)}
                    helperText="Alert when projected monthly cost exceeds this amount"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                    }}
                  />
                </Box>
              </Box>

              <Box mt={3}>
                <Button
                  variant="contained"
                  onClick={handleUpdateThresholds}
                  disabled={loading}
                >
                  Update Thresholds
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Important Notes */}
        <Box sx={{ width: '100%' }}>
          <Alert severity="info">
            <Typography variant="subtitle2" gutterBottom>
              Important Notes:
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Usage data is cached for 5 minutes to reduce API calls</li>
                <li>Claude Admin API key (starting with sk-ant-admin...) is required for detailed usage tracking</li>
                <li>OpenAI usage tracking uses standard API keys</li>
                <li>Neither provider supports checking current credit balance via API</li>
                <li>Set up auto-reload in provider consoles to avoid service interruptions</li>
              </ul>
            </Typography>
          </Alert>
        </Box>

        {/* Configure Admin Key Dialog */}
        <Dialog
          open={adminKeyDialogOpen}
          onClose={() => !loading && setAdminKeyDialogOpen(false)}
          maxWidth="sm"
          fullWidth>
          <DialogTitle>Configure Claude Admin API Key</DialogTitle>
          <DialogContent>
            <Box pt={2}>
              <TextField
                fullWidth
                label="Admin API Key"
                placeholder="sk-ant-admin..."
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                helperText="Enter your Claude Admin API key (must start with sk-ant-admin)"
                type="password"
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Only organization admins can create Admin API keys through the Claude Console at{' '}
                  <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">
                    console.anthropic.com
                  </a>
                </Typography>
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdminKeyDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfigureAdminKey}
              variant="contained"
              disabled={loading || !adminKey || !adminKey.startsWith('sk-ant-admin')}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

export default AIUsageConfiguration;
